import React, { useState, useEffect } from 'react';
import { glassSurface } from './config/glassTheme';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API from './api';

const PLATFORMS = [
  { id: 'twitter',   label: 'X / Twitter',  icon: '𝕏' },
  { id: 'instagram', label: 'Instagram',     icon: '📷' },
  { id: 'facebook',  label: 'Facebook',      icon: '📘' },
  { id: 'tiktok',    label: 'TikTok',        icon: '🎵' },
  { id: 'reddit',    label: 'Reddit',        icon: '🔴' },
  { id: 'youtube',   label: 'YouTube',       icon: '▶' },
  { id: 'other',     label: 'Other',         icon: '🔗' },
];

const T = {
  bg: '#f4f6f9', surface: '#ffffff', border: '#e2e6ea', borderSoft: '#eef0f3',
  text: '#1a202c', textMid: '#2d3748', textSub: '#718096', textFaint: '#a0aec0',
  accent: '#2b6cb0', accentLight: '#ebf4ff', accentBorder: '#bee3f8',
  green: '#276749', greenBg: '#f0fff4', greenBorder: '#9ae6b4',
  red: '#c53030', redBg: '#fff5f5', redBorder: '#feb2b2',
  amber: '#b7791f', amberBg: '#fffaf0', amberBorder: '#fbd38d',
};
const font = "'Inter','Helvetica Neue',Arial,sans-serif";
const mono = "'JetBrains Mono','Fira Code',monospace";

const s = {
  root: { minHeight: '100vh', background: T.bg, fontFamily: font, color: T.text, WebkitFontSmoothing: 'antialiased' },
  topbar: { background: T.surface, borderBottom: `1px solid ${T.border}`, padding: '0 32px', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 50, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  content: { maxWidth: 860, margin: '0 auto', padding: '32px 24px' },
  card: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: '22px 24px', marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  cardTitle: { fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 4 },
  cardSub: { fontSize: 12, color: T.textSub, marginBottom: 18 },
  label: { fontSize: 11, fontWeight: 600, color: T.textSub, marginBottom: 5, display: 'block' },
  input: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: '8px 11px', color: T.text, fontSize: 13, outline: 'none', fontFamily: font, width: '100%', boxSizing: 'border-box' },
  btn: (v) => ({ padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: font, display: 'inline-flex', alignItems: 'center', gap: 5, ...(v === 'primary' ? glassSurface(T.accent, T.accent, T.accent) : { border: `1px solid ${T.border}`, background: T.surface, color: T.textMid }) }),
  badge: (c) => ({ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: c === 'green' ? T.greenBg : c === 'amber' ? T.amberBg : c === 'red' ? T.redBg : T.accentLight, color: c === 'green' ? T.green : c === 'amber' ? T.amber : c === 'red' ? T.red : T.accent, border: `1px solid ${c === 'green' ? T.greenBorder : c === 'amber' ? T.amberBorder : c === 'red' ? T.redBorder : T.accentBorder}` }),
  statRow: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 20 },
  stat: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: '14px 16px', textAlign: 'center' },
  statVal: { fontSize: 26, fontWeight: 800, color: T.text, lineHeight: 1 },
  statLabel: { fontSize: 10, fontWeight: 600, color: T.textSub, textTransform: 'uppercase', letterSpacing: '0.07em', marginTop: 5 },
  referralBox: { background: T.accentLight, border: `1px solid ${T.accentBorder}`, borderRadius: 8, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 },
  codeBox: { fontFamily: mono, fontSize: 14, fontWeight: 700, color: T.accent, background: T.surface, border: `1px solid ${T.accentBorder}`, borderRadius: 5, padding: '6px 12px', letterSpacing: '0.08em', flex: 1 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th: { textAlign: 'left', padding: '8px 12px', fontSize: 10, fontWeight: 700, color: T.textSub, textTransform: 'uppercase', letterSpacing: '0.07em', background: T.bg, borderBottom: `1px solid ${T.border}` },
  td: { padding: '10px 12px', borderBottom: `1px solid ${T.borderSoft}`, color: T.textMid, verticalAlign: 'middle' },
  empty: { padding: '28px 0', textAlign: 'center', color: T.textFaint, fontSize: 12 },
  error: { padding: '10px 13px', background: T.redBg, border: `1px solid ${T.redBorder}`, borderRadius: 6, color: T.red, fontSize: 12, fontWeight: 600, marginBottom: 12 },
  success: { padding: '10px 13px', background: T.greenBg, border: `1px solid ${T.greenBorder}`, borderRadius: 6, color: T.green, fontSize: 12, fontWeight: 600, marginBottom: 12 },
  platformGrid: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 14 },
  platformBtn: (sel) => ({ padding: '8px 6px', borderRadius: 7, border: `2px solid ${sel ? T.accent : T.border}`, background: sel ? T.accentLight : T.surface, cursor: 'pointer', textAlign: 'center', transition: 'all 0.1s' }),
  platformIcon: { fontSize: 18, display: 'block', marginBottom: 3 },
  platformLabel: (sel) => ({ fontSize: 10, fontWeight: 600, color: sel ? T.accent : T.textSub }),
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-AU', { dateStyle: 'short' });
}

function statusColor(st) {
  if (st === 'PENDING') return 'amber';
  if (st === 'APPROVED') return 'green';
  if (st === 'REJECTED') return 'red';
  if (st === 'QUALIFIED') return 'green';
  return 'blue';
}

export default function RewardsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [platform, setPlatform] = useState('');
  const [url, setUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState(null);
  const [submitOk, setSubmitOk] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) return;
    fetch(`${API}/api/rewards/my`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [user]);

  const copyCode = () => {
    if (!data?.referral_url) return;
    navigator.clipboard.writeText(data.referral_url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleSubmit = () => {
    if (!platform) { setSubmitErr('Please select a platform.'); return; }
    if (!url.trim()) { setSubmitErr('Please enter a post URL.'); return; }
    setSubmitting(true); setSubmitErr(null); setSubmitOk(null);
    fetch(`${API}/api/rewards/submit`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ platform, url: url.trim() }),
    }).then(async r => {
      const d = await r.json();
      if (!r.ok) { setSubmitErr(d.error || 'Submission failed'); }
      else {
        setSubmitOk(d.message);
        setPlatform(''); setUrl('');
        // Refresh data
        fetch(`${API}/api/rewards/my`, { credentials: 'include' })
          .then(r2 => r2.json()).then(setData);
      }
    }).catch(() => setSubmitErr('Network error'))
      .finally(() => setSubmitting(false));
  };

  if (!user) return null;

  return (
    <div style={s.root}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;600&display=swap" rel="stylesheet" />
      <div style={s.topbar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button style={{ ...s.btn(), fontSize: 12, padding: '5px 11px' }} onClick={() => navigate('/dashboard')}>← Dashboard</button>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Rewards & Referrals</div>
            <div style={{ fontSize: 11, color: T.textSub }}>Earn credits by sharing VISINVOKE</div>
          </div>
        </div>
        <div style={{ fontSize: 11, color: T.textSub }}>{user.email}</div>
      </div>

      <div style={s.content}>

        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: T.textSub, fontSize: 13 }}>Loading…</div>
        )}

        {!loading && data && (
          <>
            {/* Stats */}
            <div style={s.statRow}>
              <div style={s.stat}>
                <div style={s.statVal}>{data.referral_summary?.total ?? 0}</div>
                <div style={s.statLabel}>Referrals Made</div>
              </div>
              <div style={s.stat}>
                <div style={{ ...s.statVal, color: T.green }}>{data.referral_summary?.qualified ?? 0}</div>
                <div style={s.statLabel}>Qualified</div>
              </div>
              <div style={s.stat}>
                <div style={{ ...s.statVal, color: T.accent }}>{data.referral_summary?.rewards_granted ?? 0}</div>
                <div style={s.statLabel}>Rewards Granted</div>
              </div>
            </div>

            {/* Referral link */}
            <div style={s.card}>
              <div style={s.cardTitle}>Your Referral Link</div>
              <div style={s.cardSub}>Share this link — when someone signs up and subscribes, you both earn credits.</div>
              <div style={s.referralBox}>
                <div style={s.codeBox}>{data.referral_url || 'No referral code assigned'}</div>
                <button style={s.btn('primary')} onClick={copyCode}>
                  {copied ? '✓ Copied' : 'Copy Link'}
                </button>
              </div>
              <div style={{ fontSize: 11, color: T.textSub }}>
                Your referral code: <code style={{ fontFamily: mono, fontWeight: 700, color: T.accent }}>{data.referral_code || '—'}</code>
                {data.was_referred && (
                  <span style={{ marginLeft: 16, color: T.green, fontWeight: 600 }}>✓ You were referred by another user</span>
                )}
              </div>
            </div>

            {/* How it works */}
            <div style={s.card}>
              <div style={s.cardTitle}>How Rewards Work</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                {[
                  { icon: '🔗', title: 'Share Your Link', desc: 'Send your referral link to friends, fellow creators, or your community.' },
                  { icon: '✍️', title: 'They Sign Up', desc: 'They register using your link — the referral is automatically tracked.' },
                  { icon: '✓', title: 'They Subscribe', desc: 'Once they start a paid subscription, your referral qualifies.' },
                  { icon: '🎁', title: 'You Both Earn', desc: 'Our team reviews and grants credits to both of you.' },
                ].map(({ icon, title, desc }) => (
                  <div key={title} style={{ display: 'flex', gap: 10, padding: '10px 12px', background: T.bg, borderRadius: 7, border: `1px solid ${T.borderSoft}` }}>
                    <span style={{ fontSize: 20 }}>{icon}</span>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 2 }}>{title}</div>
                      <div style={{ fontSize: 11, color: T.textSub, lineHeight: 1.5 }}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Social submission */}
            <div style={s.card}>
              <div style={s.cardTitle}>Submit a Social Post</div>
              <div style={s.cardSub}>Share VISINVOKE on social media and submit the post for credit rewards. Max {3} submissions per day.</div>

              {submitOk && <div style={s.success}>✓ {submitOk}</div>}
              {submitErr && <div style={s.error}>⚠ {submitErr}</div>}

              <div style={{ marginBottom: 14 }}>
                <label style={s.label}>Platform *</label>
                <div style={s.platformGrid}>
                  {PLATFORMS.map(p => (
                    <div key={p.id} style={s.platformBtn(platform === p.id)} onClick={() => setPlatform(p.id)}>
                      <span style={s.platformIcon}>{p.icon}</span>
                      <span style={s.platformLabel(platform === p.id)}>{p.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={s.label}>Post URL *</label>
                <input
                  style={s.input}
                  placeholder="https://twitter.com/yourhandle/status/..."
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                />
              </div>

              <button
                style={{ ...s.btn('primary'), padding: '9px 20px' }}
                onClick={handleSubmit}
                disabled={submitting || !platform || !url.trim()}
              >
                {submitting ? 'Submitting…' : 'Submit for Review'}
              </button>
            </div>

            {/* Referral history */}
            {data.referrals?.length > 0 && (
              <div style={s.card}>
                <div style={s.cardTitle}>Referral History</div>
                <table style={{ ...s.table, marginTop: 12 }}>
                  <thead>
                    <tr>
                      {['Date', 'Referred User', 'Status', 'Qualifying Event', 'Reward'].map(h => (
                        <th key={h} style={s.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.referrals.map((r, i) => (
                      <tr key={i}>
                        <td style={{ ...s.td, fontSize: 11 }}>{fmtDate(r.created_at)}</td>
                        <td style={s.td}><code style={{ fontFamily: mono, fontSize: 10, color: T.textSub }}>{r.referred_user_id?.slice(0, 14)}…</code></td>
                        <td style={s.td}><span style={s.badge(statusColor(r.status))}>{r.status}</span></td>
                        <td style={{ ...s.td, fontSize: 11, color: T.textSub }}>{r.qualifying_event || '—'}</td>
                        <td style={s.td}>{r.reward_granted ? <span style={s.badge('green')}>✓ Granted</span> : <span style={{ fontSize: 11, color: T.textFaint }}>Pending</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Submission history */}
            <div style={s.card}>
              <div style={s.cardTitle}>Submission History</div>
              {data.submissions?.length === 0 ? (
                <div style={s.empty}>No submissions yet — share your work and submit a post above.</div>
              ) : (
                <table style={{ ...s.table, marginTop: 12 }}>
                  <thead>
                    <tr>
                      {['Date', 'Platform', 'URL', 'Status', 'Reward'].map(h => (
                        <th key={h} style={s.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.submissions.map((sub, i) => (
                      <tr key={i}>
                        <td style={{ ...s.td, fontSize: 11, whiteSpace: 'nowrap' }}>{fmtDate(sub.created_at)}</td>
                        <td style={s.td}><span style={s.badge('blue')}>{sub.platform}</span></td>
                        <td style={{ ...s.td, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <a href={sub.url} target="_blank" rel="noopener noreferrer" style={{ color: T.accent, fontSize: 11 }}>{sub.url}</a>
                        </td>
                        <td style={s.td}><span style={s.badge(statusColor(sub.status))}>{sub.status}</span></td>
                        <td style={s.td}>
                          {sub.reward_value ? (
                            <span style={s.badge('green')}>{sub.reward_value} credits</span>
                          ) : (
                            <span style={{ fontSize: 11, color: T.textFaint }}>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
